// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { SolidBrush } from '@shared/commons/Graphics';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { Scroll, VisualControlState } from '@front/controls/elements/Scroll';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { DynProperty } from '@shared/commons/DynProperty';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
var TerceraChartScrollerRendererMouseState;
(function (TerceraChartScrollerRendererMouseState) {
    TerceraChartScrollerRendererMouseState[TerceraChartScrollerRendererMouseState["None"] = 0] = "None";
    TerceraChartScrollerRendererMouseState[TerceraChartScrollerRendererMouseState["Moving"] = 1] = "Moving";
})(TerceraChartScrollerRendererMouseState || (TerceraChartScrollerRendererMouseState = {}));
export class TerceraChartScrollerRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this.MouseState = TerceraChartScrollerRendererMouseState.None;
        this.Enabled = true;
        this.isAttached = true;
        this.scrollControlsStates = {
            leftBtn: VisualControlState.None,
            rightBtn: VisualControlState.None,
            slider: VisualControlState.None
        };
        this.Rectangle = new Rectangle();
        this.insideRect = new Rectangle();
        this.scrollRect = new Rectangle();
        this.SetClassName('TerceraChartScrollerRenderer');
    }
    ScaleKoef() {
        const seriesCount = this.chart.BarsCount();
        if (seriesCount === 0 || this.chart.mainWindow.im() > seriesCount) {
            return 1;
        }
        else {
            return this.insideRect.Width / seriesCount;
        }
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        if (!this.Visible) {
            return;
        }
        const R = this.Rectangle;
        const RX = R.X;
        const RY = R.Y;
        const RW = R.Width;
        const RH = R.Height;
        gr.FillRect(this.backBrushChartScroll, RX, RY, RW, RH);
        const paddingY = 4;
        this.insideRect = new Rectangle(RX, RY, RW, RH);
        this.insideRect.Y += paddingY;
        this.insideRect.Height -= paddingY * 2;
        const outSubj = { rightX: 0, leftX: 0 };
        if (!this.CalcScrollPosition(window, outSubj)) {
            return;
        }
        let rightX = outSubj.rightX;
        let leftX = outSubj.leftX;
        while (rightX - leftX < 8) {
            rightX++;
            leftX--;
        }
        this.scrollRect = new Rectangle(leftX, this.insideRect.Y, rightX - leftX, this.insideRect.Height);
        Scroll.drawSlider(gr, this.scrollRect, this.scrollControlsStates.slider);
    }
    CalcScrollPosition(window, outSubj) {
        if (this.chart.BarsCount() === 0) {
            outSubj.rightX = outSubj.leftX = 0;
            return false;
        }
        const scaleKoef = this.ScaleKoef();
        const insideRect = this.insideRect;
        if (scaleKoef == 1) {
            const insideRectX = insideRect.X;
            outSubj.leftX = insideRectX;
            outSubj.rightX = insideRectX + insideRect.Width;
        }
        else {
            outSubj.rightX = insideRect.X + Math.round((window.i1 + 1) * scaleKoef) + 1;
            outSubj.leftX = insideRect.X + Math.round((window.i1 + 0 - window.im()) * scaleKoef);
            if (outSubj.leftX < insideRect.X) {
                outSubj.leftX = insideRect.X;
            }
        }
        return true;
    }
    ThemeChanged() {
        super.ThemeChanged();
        const colors = ThemeManager.colors;
        this.backBrushChartScroll = new SolidBrush(this.isAttached ? colors.attachedScrollBackgroundColor : colors.detachedScrollBackgroundColor);
    }
    clearScrollVisualState() {
        const scrollControlsStates = this.scrollControlsStates;
        const keys = Object.keys(scrollControlsStates);
        for (let i = 0; i < keys.length; i++) {
            scrollControlsStates[keys[i]] = VisualControlState.None;
        }
    }
    setScrollVisualState(controlName, state) {
        this.clearScrollVisualState();
        if (controlName) {
            this.scrollControlsStates[controlName] = state;
        }
    }
    Properties() {
        const properties = super.Properties();
        const SeparatorGroup = '#3#' + Resources.getResource('property.SeparatorGroup.Additional');
        const prop = new DynProperty('ScrollBar', this.Visible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP);
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 2;
        properties.push(prop);
        return properties;
    }
    callBack(properties) {
        super.callBack(properties);
        const dp = DynProperty.getPropertyByName(properties, 'ScrollBar');
        if (dp)
            this.Visible = dp.value;
    }
    setBackBrushChartScroll(color) {
        this.backBrushChartScroll = new SolidBrush(color);
    }
    calci1(x) {
        return (x - this.insideRect.X) / this.ScaleKoef();
    }
    calcShiftByArrow(leftArrowClick) {
        let delta = this.chart.mainWindow.im() / 30.0;
        if (delta < 1)
            delta = 1.0;
        return Math.round(this.chart.mainWindow.i1 + (leftArrowClick ? -delta : delta));
    }
    calcShiftByWheel(delta) {
        const seriesCount = this.chart.BarsCount();
        if (seriesCount === 0) {
            return this.chart.mainWindow.i1;
        }
        const temp = (seriesCount - this.chart.mainWindow.im()) / 30;
        let step = ((temp != 0 ? temp : 1) * delta) / 120;
        if (step == 0) {
            step = 1;
        }
        return this.chart.mainWindow.i1 + step;
    }
    ProcessMouseMove(e) {
        if (!this.Enabled) {
            return false;
        }
        const result = false;
        if (this.MouseState === TerceraChartScrollerRendererMouseState.Moving) {
            const newI1 = this.calci1(e.X - this.lastMouseDownDeltaX);
            if (this.chart.moveScreenByX(newI1)) {
                e.NeedRedraw = true;
            }
        }
        this.clearScrollVisualState();
        if (this.scrollRect.Contains(e.X, e.Y)) {
            this.setScrollVisualState('slider', VisualControlState.Hovered);
        }
        return result;
    }
    ProcessMouseDown(e) {
        if (!this.Enabled || e.Button != MouseButtons.Left) {
            return false;
        }
        let res = this.Rectangle.Contains(e.X, e.Y);
        const scrollRect = this.scrollRect;
        const tumbClick = scrollRect.Contains(e.X, e.Y);
        if (tumbClick) {
            this.MouseState = TerceraChartScrollerRendererMouseState.Moving;
            this.setScrollVisualState('slider', VisualControlState.Clicked);
        }
        else {
            this.MouseState = TerceraChartScrollerRendererMouseState.None;
        }
        const scrollRectX = scrollRect.X;
        this.lastMouseDownDeltaX = e.Location.X - scrollRectX - scrollRect.Width;
        this.lastMouseDownState = e.Location;
        if (this.insideRect.Contains(e.Location.X, e.Location.Y) && !tumbClick) {
            const newI1 = this.calci1(e.X);
            if (this.chart.moveScreenByX(newI1)) {
                e.NeedRedraw = true;
            }
            this.MouseState = TerceraChartScrollerRendererMouseState.None;
            res = true;
        }
        return res;
    }
    ProcessMouseUp(e) {
        super.ProcessMouseUp(e);
        this.MouseState = TerceraChartScrollerRendererMouseState.None;
        this.clearScrollVisualState();
        return false;
    }
    ProcessMouseLeave(e) {
        super.ProcessMouseLeave(e);
        this.clearScrollVisualState();
    }
    ProcessMouseWheel(e) {
        if (Math.abs(e._baseEvent.deltaX) > Math.abs(e._baseEvent.deltaY)) {
            const xDelta = e._baseEvent.deltaX;
            if (xDelta !== 0) {
                const coefSensitivity = 0.7;
                const coefBarW = (xDelta / this.chart.mainWindow.XScale) * coefSensitivity;
                if (this.chart.moveScreenByX(this.chart.rightBarIndexIncludingEmptyBars + coefBarW)) {
                    this.chart.checkBarsCountAfterEvents();
                    e.NeedRedraw = true;
                }
            }
            return true;
        }
        if (this.Enabled) {
            const newI1 = this.calcShiftByWheel(e.Delta);
            this.chart.moveScreenByX(newI1);
            return true;
        }
        return false;
    }
    TurnOffRenderer() {
        this.Visible = false;
        this.Enabled = false;
    }
}
TerceraChartScrollerRenderer.SCROLLER_HEIGHT = 16;
